import { config } from './config';
import { getIdToken } from './auth';

export class ApiError extends Error {
  constructor(public readonly status: number, message: string, public readonly body?: unknown) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = await getIdToken();
  if (!token) throw new ApiError(401, 'Not signed in');

  const res = await fetch(`${config.apiUrl}${path}`, {
    method,
    headers: {
      authorization: `Bearer ${token}`,
      ...(body ? { 'content-type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : undefined;
  if (!res.ok) {
    const message =
      data && typeof data === 'object' && 'error' in data && typeof (data as { error: unknown }).error === 'string'
        ? (data as { error: string }).error
        : `Request failed: ${res.status}`;
    throw new ApiError(res.status, message, data);
  }
  return data as T;
}

export const api = {
  me: () =>
    request<{ email: string; role: 'Admin' | 'SafetyNet'; permissions: string[] }>('GET', '/me'),
  listAdminUsers: () => request<{ items: AdminUser[] }>('GET', '/admin-users'),
  addAdminUser: (email: string, role: 'Admin' | 'SafetyNet') =>
    request<AdminUser>('POST', '/admin-users', { email, role }),
  updateAdminUser: (
    email: string,
    updates: {
      role?: 'Admin' | 'SafetyNet';
      grantedPermissions?: string[];
      deniedPermissions?: string[];
    },
  ) => request<AdminUser>('PATCH', `/admin-users/${encodeURIComponent(email)}`, updates),
  deleteAdminUser: (email: string) =>
    request<void>('DELETE', `/admin-users/${encodeURIComponent(email)}`),
  getAdminUserCognito: (email: string) =>
    request<AdminUserCognito>('GET', `/admin-users/${encodeURIComponent(email)}/cognito`),

  listOrganizations: () =>
    request<{ items: Organization[]; truncated: boolean }>('GET', '/organizations'),
  createOrganization: (input: { name: string; company?: string; description?: string }) =>
    request<{ item: Organization }>('POST', '/organizations', input),
  getOrganization: (id: string) =>
    request<{ organization: Organization; users: X5User[]; applications: X5Application[] }>(
      'GET',
      `/organizations/${id}`,
    ),
  updateOrganization: (id: string, updates: OrganizationEdit) =>
    request<{ item: Organization; changed: OrganizationFieldChange[] }>(
      'PATCH',
      `/organizations/${encodeURIComponent(id)}`,
      updates,
    ),
  updateOrganizationMembers: (id: string, changes: OrganizationMemberChanges) =>
    request<OrganizationMembersResult>(
      'POST',
      `/organizations/${encodeURIComponent(id)}/members`,
      changes,
    ),
  listUsers: () => request<{ items: X5User[] }>('GET', '/users'),
  createUser: (input: { email: string; username: string; organizationId?: string | null }) =>
    request<X5UserCreated>('POST', '/users', input),
  updateUser: (id: string, updates: { organizationId: string | null }) =>
    request<{ item: X5User }>('PATCH', `/users/${encodeURIComponent(id)}`, updates),
  listUserIdentities: () =>
    request<{ items: X5UserIdentity[] }>('GET', '/users/identities'),
  getUserIdentity: (username: string) =>
    request<X5UserIdentityDetail>(
      'GET',
      `/users/${encodeURIComponent(username)}/identity`,
    ),
  listApplications: () =>
    request<{ items: X5Application[]; serversPartial?: boolean }>('GET', '/applications'),

  listDatalakeRuns: (limit = 50) =>
    request<{ items: DatalakeRunSummary[] }>('GET', `/datalake/runs?limit=${limit}`),
  getDatalakeRun: (runId: string) =>
    request<{ s3Key: string; lastModified: string | null; body: DatalakeRunLog }>(
      'GET',
      `/datalake/runs/${encodeURIComponent(runId)}`,
    ),
  listDatalakeExecutions: () =>
    request<{ running: DatalakeExecution[]; recent: DatalakeExecution[] }>(
      'GET',
      '/datalake/executions',
    ),
  getDatalakeMetrics: (days = 14) =>
    request<{ series: DatalakeMetricSeries[] }>('GET', `/datalake/metrics?days=${days}`),
  getDatalakeClients: () => request<{ clients: string[] }>('GET', '/datalake/clients'),
  getDatalakeCoverage: (days = 60) =>
    request<DatalakeCoverage>('GET', `/datalake/coverage?days=${days}`),
  getDatalakeCustomers: (days = 30) =>
    request<DatalakeFleetHealth>('GET', `/datalake/customers?days=${days}`),
  getDatalakeExecutionDetail: (executionArn: string) =>
    request<DatalakeExecutionDetail>(
      'GET',
      `/datalake/executions/${encodeURIComponent(executionArn)}`,
    ),

  listReportRuns: (opts?: { reportId?: string; days?: number | 'all'; limit?: number }) => {
    const params = new URLSearchParams();
    if (opts?.reportId) params.set('report_id', opts.reportId);
    if (opts?.days !== undefined) params.set('days', String(opts.days));
    if (opts?.limit !== undefined) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return request<{ items: ReportRun[]; truncated?: boolean; lookbackDays?: number | null }>(
      'GET',
      qs ? `/reports/runs?${qs}` : '/reports/runs',
    );
  },
  getReportRun: (reportId: string, runTimestamp: string) =>
    request<ReportRun>(
      'GET',
      `/reports/runs/${encodeURIComponent(reportId)}/${encodeURIComponent(runTimestamp)}`,
    ),
  listReportConfigs: () =>
    request<{ items: ReportConfig[]; truncated?: boolean }>('GET', '/reports/configs'),
  getReportsSummary: () => request<ReportsSummary>('GET', '/reports/summary'),
  previewReport: (reportId: string, date?: string) =>
    request<ReportPreview>('POST', '/reports/preview', { report_id: reportId, date }),
  runReportNow: (reportId: string, date: string) =>
    request<ReportRunQueued>('POST', '/reports/run', { report_id: reportId, date }),

  listExports: (params: { clientId?: string; cursor?: string } = {}) => {
    const search = new URLSearchParams();
    if (params.clientId) search.set('client_id', params.clientId);
    if (params.cursor) search.set('cursor', params.cursor);
    const qs = search.toString();
    return request<ExportHistoryPage>('GET', `/exporter/exports${qs ? `?${qs}` : ''}`);
  },
  triggerExport: (clientId: string) =>
    request<ExportQueued>('POST', '/exporter/exports', { client_id: clientId }),
  deleteExport: (clientId: string, exportTimestamp: string) =>
    request<ExportDeleted>(
      'DELETE',
      `/exporter/exports?client_id=${encodeURIComponent(clientId)}&export_timestamp=${encodeURIComponent(exportTimestamp)}`,
    ),
  generateExportLinks: (input: ExportLinksRequest) =>
    request<ExportLinks>('POST', '/exporter/links', input),
  listExportLinks: (clientId: string, exportTimestamp: string) =>
    request<{ items: IssuedExportLink[]; truncated: boolean }>(
      'GET',
      `/exporter/links?client_id=${encodeURIComponent(clientId)}&export_timestamp=${encodeURIComponent(exportTimestamp)}`,
    ),
  revokeExportLink: (clientId: string, exportTimestamp: string, issuedAt: string) =>
    request<ExportLinkRevoked>('POST', '/exporter/links/revoke', {
      client_id: clientId,
      export_timestamp: exportTimestamp,
      issued_at: issuedAt,
    }),
  listRecentExportLinks: (limit = 50) =>
    request<{ items: IssuedExportLink[]; truncated: boolean; scanned_through: string }>(
      'GET',
      `/exporter/links?limit=${limit}`,
    ),
  getExportAccessLogs: (clientId: string, exportTimestamp: string) =>
    request<{ items: ExportAccessLogEntry[]; truncated: boolean; enabled: boolean }>(
      'GET',
      `/exporter/access-logs?client_id=${encodeURIComponent(clientId)}&export_timestamp=${encodeURIComponent(exportTimestamp)}`,
    ),

  listAppStreamSessions: () => request<AppStreamSessions>('GET', '/appstream/sessions'),
  terminateAppStreamSession: (sessionId: string) =>
    request<AppStreamSessionTerminated>(
      'DELETE',
      `/appstream/sessions?session_id=${encodeURIComponent(sessionId)}`,
    ),
  getAppStreamUsage: (days = 30) =>
    request<AppStreamUsage>('GET', `/appstream/usage?days=${days}`),

  listSmsNumbers: () => request<SmsNumbers>('GET', '/sms/numbers'),
  putSmsAssignment: (input: {
    phone_number: string;
    server_ip: string;
    replaces?: { server_ip: string; phone_number: string };
  }) => request<SmsAssignmentSaved>('POST', '/sms/assignments', input),
  acquireSmsNumber: (registrationId: string, clientToken: string) =>
    request<SmsNumberAcquired>('POST', '/sms/numbers', {
      registration_id: registrationId,
      client_token: clientToken,
    }),
  setSmsTwoWay: (phoneNumber: string, enabled: boolean) =>
    request<SmsTwoWayUpdated>('PATCH', '/sms/numbers', {
      phone_number: phoneNumber,
      two_way_enabled: enabled,
    }),
  /** `since`: a refresh finished at or after this epoch ms, so the server must not serve an older copy. */
  listCellularSims: (since?: number) => request<CellularSims>('GET', `/cellular/sims${since ? `?since=${since}` : ''}`),
  checkCellularWan: (deviceId: number) =>
    request<CellularWanCheck>('POST', `/cellular/sims/${deviceId}/wan-check`),
  listGateways: (since?: number) => request<GatewayFleet>('GET', `/gateways${since ? `?since=${since}` : ''}`),
  /** `runId: null, fresh: true` — refreshed within the last minute; nothing to wait for. */
  startSnapshotRefresh: () => request<{ runId: string | null; fresh: boolean }>('POST', '/snapshot/refresh'),
  getSnapshotRefresh: (runId: string) =>
    request<RefreshProgress>('GET', `/snapshot/refresh/${encodeURIComponent(runId)}`),
  listRecentGatewayCommands: (days: number) =>
    request<GatewayCommandLog>('GET', `/gateways/commands?days=${days}`),
  listGatewayCommands: (routerId: number) =>
    request<{ items: GatewayCommandRecord[] }>('GET', `/gateways/${routerId}/commands`),
  sendGatewayCommand: (
    routerId: number,
    body: { action: GatewayActionId; route: GatewayRoute; reason?: string; password?: string },
  ) => request<GatewaySendResult>('POST', `/gateways/${routerId}/commands`, body),
  getCellularWanCheck: (channel: string, routerId: number) =>
    request<CellularWanCheck>(
      'GET',
      `/cellular/wan-check?channel=${encodeURIComponent(channel)}&router=${routerId}`,
    ),
  listCellularNotices: () => request<CellularNoticeList>('GET', '/cellular/notices'),
  getCellularNotice: (customerKey: string) =>
    request<CellularNoticeDetail>('GET', `/cellular/notices/${encodeURIComponent(customerKey)}`),
  decideCellularNotice: (
    customerKey: string,
    input: { decision: NoticeState; recipients?: NoticeRecipient[]; reason?: string },
  ) =>
    request<{ notice: NoticeCase }>(
      'POST',
      `/cellular/notices/${encodeURIComponent(customerKey)}/decision`,
      input,
    ),
  testCellularNotice: (customerKey: string) =>
    request<{ sentTo: string; messageId: string }>(
      'POST',
      `/cellular/notices/${encodeURIComponent(customerKey)}/test`,
    ),
  deleteSmsAssignment: (serverIp: string, phoneNumber: string) =>
    request<SmsAssignmentRemoved>(
      'DELETE',
      `/sms/assignments?server_ip=${encodeURIComponent(serverIp)}&phone_number=${encodeURIComponent(phoneNumber)}`,
    ),

  listRoles: () => request<{ items: Role[] }>('GET', '/roles'),
  listAuditLog: (params: {
    limit?: number;
    cursor?: string;
    actor?: string;
    action?: string;
  } = {}) => {
    const search = new URLSearchParams();
    if (params.limit) search.set('limit', String(params.limit));
    if (params.cursor) search.set('cursor', params.cursor);
    if (params.actor) search.set('actor', params.actor);
    if (params.action) search.set('action', params.action);
    const qs = search.toString();
    return request<AuditPage>('GET', `/audit${qs ? `?${qs}` : ''}`);
  },
};

export interface Role {
  roleId: 'Admin' | 'SafetyNet';
  name: string;
  description: string;
  permissions: string[];
  builtIn: boolean;
}

export interface AuditEntry {
  day: string;
  occurredAt: string;
  timestamp: string;
  actor: string;
  actorRole: string;
  action: string;
  outcome: 'requested' | 'success' | 'error' | 'denied';
  target: string;
  route: string;
  sourceIp: string;
  userAgent: string;
  requestId: string;
  details?: Record<string, unknown>;
}

export interface AuditPage {
  items: AuditEntry[];
  nextCursor: string | null;
  /** The query budget ran out before `limit` rows were found, not the log. */
  truncated: boolean;
}

export interface AdminUser {
  email: string;
  role: 'Admin' | 'SafetyNet';
  addedBy: string;
  createdAt: string;
  updatedAt: string;
  grantedPermissions?: string[];
  deniedPermissions?: string[];
}

export interface AdminUserCognitoAuthEvent {
  eventId: string | null;
  eventType: string | null;
  creationDate: string | null;
  eventResponse: string | null;
  riskDecision: string | null;
  riskLevel: string | null;
  ipAddress: string | null;
  city: string | null;
  country: string | null;
  deviceName: string | null;
}

export type AdminUserCognito =
  | { exists: false }
  | {
      exists: true;
      username: string | null;
      status: string | null;
      enabled: boolean | null;
      userCreateDate: string | null;
      userLastModifiedDate: string | null;
      attributes: Record<string, string>;
      authEvents: AdminUserCognitoAuthEvent[];
      authEventsError: string | null;
    };

export interface Organization {
  id: string;
  name?: string;
  company?: string;
  description?: string | null;
  createdAt?: string;
  updatedAt?: string;
  /** Amplify bookkeeping, bumped on every write so DataStore clients resync. */
  _version?: number;
  _lastChangedAt?: number;
}

/** Mirrors `MAX_ORGANIZATIONS` in api/src/handlers/organizations.ts. */
export const MAX_ORGANIZATIONS = 500;

export type OrganizationField = 'name' | 'company' | 'description';

/** Only the keys present are touched; `null` clears the field. */
export type OrganizationEdit = Partial<Record<OrganizationField, string | null>>;

export interface OrganizationFieldChange {
  field: OrganizationField;
  from: string | null;
  to: string | null;
}

export interface OrganizationMemberChanges {
  addUserIds?: string[];
  removeUserIds?: string[];
  addApplicationIds?: string[];
  removeApplicationIds?: string[];
}

export interface OrganizationMemberOp {
  id: string;
  label: string | null;
  /** Where it came from — non-null on an add means this was a move. */
  previousOrganizationId: string | null;
}

export interface OrganizationMemberFailure {
  id: string;
  kind: 'user' | 'application';
  action: 'add' | 'remove';
  reason: string;
}

export interface OrganizationMembersResult {
  organizationId: string;
  applied: {
    usersAdded: OrganizationMemberOp[];
    usersRemoved: OrganizationMemberOp[];
    applicationsAdded: OrganizationMemberOp[];
    applicationsRemoved: OrganizationMemberOp[];
  };
  failed: OrganizationMemberFailure[];
}

export interface X5User {
  id: string;
  username?: string;
  organizationId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface X5UserCreated {
  username: string;
  email: string;
  temporaryPassword: string;
  userId: string;
  organizationId: string | null;
  createdAt: string;
  databaseError: boolean;
}

export interface X5UserIdentity {
  username: string;
  email: string | null;
  emailVerified: boolean | null;
  status: string | null;
  enabled: boolean | null;
  userCreateDate: string | null;
  userLastModifiedDate: string | null;
}

export interface X5UserAuthEvent {
  eventId: string | null;
  eventType: string | null;
  creationDate: string | null;
  eventResponse: string | null;
  riskDecision: string | null;
  riskLevel: string | null;
  ipAddress: string | null;
  city: string | null;
  country: string | null;
  deviceName: string | null;
}

export type X5UserIdentityDetail =
  | { exists: false }
  | {
      exists: true;
      username: string | null;
      status: string | null;
      enabled: boolean | null;
      userCreateDate: string | null;
      userLastModifiedDate: string | null;
      attributes: Record<string, string>;
      authEvents: X5UserAuthEvent[];
      authEventsError: string | null;
    };

export interface X5Application {
  id: string;
  organizationId?: string;
  application?: string;
  displayName?: string;
  stack?: string;
  fleet?: string;
  /** Stored as a URL, e.g. `https://10.36.251.78:8443`. */
  x5serverIP?: string;
  createdAt?: string;
  updatedAt?: string;
  /**
   * The EC2 instance behind `x5serverIP`, resolved server-side. Null when the
   * address does not parse or no instance in the estate holds it.
   */
  server?: Ec2Server | null;
}

export type DatalakeRunStatus = 'success' | 'partial' | 'failed' | 'no-work' | 'unknown';

export interface DatalakeRunSummary {
  runId: string | null;
  s3Key: string;
  lastModified: string | null;
  size: number;
  timestamp: string | null;
  serversFound: number | null;
  serversWithWork: number | null;
  serversFailedDiscovery: number | null;
  failedServerNames: string[] | null;
  totalDates: number | null;
  succeeded: number | null;
  failed: number | null;
  emptyDates: number | null;
  emptyServers: { clientName: string; datesEmpty: number }[] | null;
  rawBytes: number | null;
  parquetBytes: number | null;
  compressionRatio: string | null;
  status: DatalakeRunStatus;
}

export interface DatalakeRunServer {
  clientName: string;
  datesProcessed?: number;
  datesSucceeded?: number;
  datesFailed?: number;
  datesEmpty?: number;
  rawBytes?: number;
  parquetBytes?: number;
  errors?: { date?: string; error?: string }[];
}

export interface DatalakeRunFailedServer {
  clientName?: string;
  serverIp?: string;
  reason?: string;
  detail?: string;
}

export interface DatalakeRunLog {
  runId?: string;
  timestamp?: string;
  discovery?: {
    totalServersFound?: number;
    serversWithWork?: number;
    serversUpToDate?: number;
    serversFailedDiscovery?: number;
    upToDateServers?: string[];
    failedServers?: DatalakeRunFailedServer[];
  };
  processing?: {
    totalDates?: number;
    succeeded?: number;
    failed?: number;
    empty?: number;
    serversWithNoSensorData?: { clientName: string; datesEmpty: number }[];
    totalRawBytes?: number;
    totalParquetBytes?: number;
    compressionRatio?: string;
  };
  servers?: DatalakeRunServer[];
  config?: Record<string, unknown>;
}

export interface DatalakeExecution {
  executionArn: string;
  name: string;
  status: string;
  startDate: string | null;
  stopDate: string | null;
}

export interface DatalakeMetricSeries {
  label: string;
  timestamps: string[];
  values: number[];
}

export interface DatalakeCoverage {
  tables: string[];
  clients: string[];
  cells: Record<string, Record<string, string>>;
  asOf: string;
  lookbackDays: number;
}

export type DatalakeClientOutcome =
  | 'ok'
  | 'partial'
  | 'failed'
  | 'discovery-failed'
  | 'up-to-date'
  | 'absent'
  | 'not-in-scope';

export type DatalakeClientHealth =
  | 'healthy'
  | 'degraded'
  | 'failing'
  | 'stale'
  | 'absent'
  | 'unknown';

export interface DatalakeClientRunPoint {
  runId: string;
  s3Key: string;
  timestamp: string | null;
  outcome: DatalakeClientOutcome;
  serverIp: string | null;
  datesSucceeded: number;
  datesFailed: number;
  datesEmpty: number;
  rawBytes: number;
  parquetBytes: number;
  firstDate: string | null;
  lastDate: string | null;
  reason: string | null;
  detail: string | null;
}

export interface DatalakeClientSummary {
  clientName: string;
  serverIp: string | null;
  status: DatalakeClientHealth;
  problem: boolean;
  latestOutcome: DatalakeClientOutcome | null;
  consecutiveFailures: number;
  failingSince: string | null;
  lastError: {
    scope: 'discovery' | 'date';
    reason: string | null;
    detail: string | null;
    at: string | null;
    runId: string;
  } | null;
  lastSuccess: {
    runId: string;
    at: string | null;
    datesSucceeded: number;
    datesEmpty: number;
    rawBytes: number;
    parquetBytes: number;
    firstDate: string | null;
    lastDate: string | null;
  } | null;
  runsSinceSuccess: number | null;
  latestDataDate: string | null;
  dataLagDays: number | null;
  noDataInWindow: boolean;
  history: DatalakeClientRunPoint[];
}

export interface DatalakeFleetHealth {
  clients: DatalakeClientSummary[];
  summary: {
    clients: number;
    healthy: number;
    degraded: number;
    failing: number;
    stale: number;
    absent: number;
    runs: number;
    partialScopeRuns: number;
    windowStart: string | null;
    windowEnd: string | null;
  };
  asOf: string;
  lookbackDays: number;
  coverageLookbackDays: number | null;
  coverageAvailable: boolean;
  truncated: boolean;
}

export interface DatalakeStateSpan {
  name: string;
  entered: string | null;
  exited: string | null;
  durationMs: number | null;
  status: 'succeeded' | 'failed' | 'in-progress';
}

export interface DatalakeExecutionFailure {
  id: number | null;
  timestamp: string | null;
  type: string | null;
  cause: string | null;
  error: string | null;
}

export interface DatalakeExecutionDetail {
  executionArn: string;
  name: string | null;
  status: string | null;
  startDate: string | null;
  stopDate: string | null;
  stateMachineArn: string | null;
  eventCount: number;
  truncated: boolean;
  states: DatalakeStateSpan[];
  failures: DatalakeExecutionFailure[];
}

export type ReportRunStatus = 'SUCCESS' | 'FAILED';

export interface ReportRun {
  report_id: string;
  run_timestamp: string;
  report_date?: string;
  status: ReportRunStatus;
  recipients?: string[];
  row_count?: number;
  duration_ms?: number;
  triggered_by?: 'scheduled' | 'manual';
  error?: string;
}

export interface ReportConfig {
  report_id: string;
  report_name?: string;
  report_template?: string;
  client?: string;
  recipients?: string[];
  date_range_days?: number;
  email_subject?: string;
  enabled?: boolean;
  schedule?: string;
}

export interface ReportsSummary {
  latest: ReportRun | null;
  failures24h: number;
  empty24h: number;
  successRate7d: number | null;
  success7d: number;
  failed7d: number;
  empty7d: number;
  configuredCount: number;
  enabledCount: number;
  daily: { date: string; succeeded: number; empty: number; failed: number }[];
}

export interface ReportPreview {
  preview: true;
  report_id: string;
  report_name: string;
  client: string;
  target_date: string;
  subject: string;
  html: string;
  csv_base64: string;
  csv_filename: string;
  row_count: number;
  duration_ms: number;
  query_execution_id: string;
}

export interface ReportRunQueued {
  queued: true;
  report_id: string;
  date: string;
  triggered_by: string;
  queued_at: string;
}

/**
 * `running` / `no-archive` are requests copy-zip hasn't (or never) answered;
 * the rest are built exports, and only `available` ones still have an archive.
 */
export type ExportStatus = 'running' | 'no-archive' | 'available' | 'expired' | 'deleted';

export interface ExportSummary {
  kind: 'export';
  client_id: string;
  export_timestamp: string;
  status: ExportStatus;
  zip_archive_key?: string;
  zip_size_bytes: number | null;
  total_archive_bytes: number | null;
  total_file_count: number | null;
  years: string[];
  archive_count: number;
  built_at: string;
  /** When the bucket's lifecycle rule removes (or removed) the archive. */
  expires_at: string;
  gone_at: string | null;
  deleted_by: string | null;
  /** Null for exports started outside the portal or before the audit log. */
  requested_by: string | null;
  requested_at: string | null;
  recorded_by: 'event' | 'listing' | 'delete' | 'backfill';
}

export interface ExportRequestSummary {
  kind: 'request';
  client_id: string;
  export_timestamp: null;
  status: ExportStatus;
  requested_by: string;
  requested_at: string;
}

export type ExportHistoryItem = ExportSummary | ExportRequestSummary;

export interface ExportHistoryPage {
  items: ExportHistoryItem[];
  next_cursor: string | null;
  retention_days: number;
}

export interface ExportQueued {
  queued: true;
  client_id: string;
  triggered_by: string;
  queued_at: string;
}

export interface ExportLinksRequest {
  client_id: string;
  export_timestamp: string;
  expiry_seconds?: number;
  send_email?: boolean;
  email_addresses?: string[];
  email_style?: 'simple' | 'branded';
}

export interface ExportLinkArchive {
  year: string;
  url: string;
  archive_key: string;
  archive_size_bytes: number;
  file_count: number;
}

export interface ExportEmailResult {
  success: boolean;
  message_id?: string;
  error_code?: string;
  error_message?: string;
  addresses: string[];
}

export interface ExportLinks {
  client_id: string;
  export_timestamp: string;
  expiry_seconds: number;
  expires_at: string;
  url: string;
  zip_archive_key: string;
  zip_size_bytes: number | null;
  archives: ExportLinkArchive[];
  email_result?: ExportEmailResult;
}

export interface ExportAccessLogEntry {
  time: string;
  remote_ip: string;
  requester: string;
  key: string;
  http_status: number | null;
  error_code: string | null;
  bytes_sent: number | null;
  object_size: number | null;
  user_agent: string;
  presigned: boolean;
  /** Ties the hit to the issuance whose `signed_at` matches. */
  signed_at: string;
}

export interface ExportDeleted {
  deleted: true;
  client_id: string;
  export_timestamp: string;
  objects_deleted: number;
  bytes_freed: number;
  /** null when the archives were deleted but their link records couldn't be updated. */
  links_revoked: number | null;
}

/**
 * Why an issuance was retired. Only `export-deleted` breaks a URL already sent —
 * an early revocation drops the portal's copy and nothing more.
 */
export type ExportLinkRevokeReason = 'export-deleted' | 'revoked-early';

export interface ExportLinkRevoked {
  revoked: true;
  client_id: string;
  export_timestamp: string;
  issued_at: string;
  revoked_at: string;
  revoked_by: string;
  /** When outstanding URLs still stop working; null if they already had. */
  urls_live_until: string | null;
}

export interface IssuedExportLinkEmail {
  addresses: string[];
  style: string;
  success: boolean;
  message_id?: string;
  error_code?: string;
  error_message?: string;
}

/** One `POST /exporter/links` call, as recorded server-side. */
export interface IssuedExportLink {
  exportId: string;
  issuedAt: string;
  timestamp: string;
  clientId: string;
  exportTimestamp: string;
  issuedBy: string;
  issuedByRole: string;
  expirySeconds: number;
  linkExpiresAt: string;
  signedAt: string;
  zipKey: string;
  zipUrl: string;
  zipSizeBytes: number | null;
  archives: ExportLinkArchive[];
  email?: IssuedExportLinkEmail;
  revokedAt?: string;
  revokedBy?: string;
  /** Always present when `revokedAt` is — the server fills it in for old rows. */
  revokedReason?: ExportLinkRevokeReason;
  status: 'active' | 'expired' | 'revoked';
  /** True when the URLs were withheld — expired, revoked, or view-only caller. */
  urlsRedacted: boolean;
}

/**
 * One live streaming session. `organizationName` is resolved server-side by
 * matching the AppStream user id against the X5 `User` roster — null means no
 * X5 user owns that username (internal staff, or a since-removed account).
 */
export interface AppStreamSession {
  id: string;
  userId: string;
  organizationId: string | null;
  organizationName: string | null;
  /** The owning organization is XiltriX staff, not a paying customer. */
  internal: boolean;
  stackName: string;
  fleetName: string;
  /** ACTIVE, PENDING or EXPIRED. */
  state: string;
  /** CONNECTED or NOT_CONNECTED — a disconnected session still holds a slot. */
  connectionState: string;
  startTime: string | null;
  maxExpirationTime: string | null;
  instanceId: string | null;
  privateIp: string | null;
  authenticationType: string | null;
}

export interface AppStreamFleet {
  name: string;
  stackNames: string[];
  state: string | null;
  instanceType: string | null;
  fleetType: string | null;
  desired: number | null;
  running: number | null;
  inUse: number | null;
  available: number | null;
  maxUserDurationSeconds: number | null;
  disconnectTimeoutSeconds: number | null;
  idleDisconnectTimeoutSeconds: number | null;
}

export interface AppStreamSessions {
  items: AppStreamSession[];
  /** Scoped to `appstream.primaryFleet` in cdk.json — capacity is only read off one. */
  fleets: AppStreamFleet[];
  asOf: string;
  truncated: boolean;
  /** The X5 roster scan capped, so some sessions may show as unattributed. */
  directoryTruncated: boolean;
}

export interface AppStreamSessionTerminated {
  terminated: true;
  sessionId: string;
  userId: string;
  fleetName: string | null;
  stackName: string | null;
  terminatedBy: string;
  terminatedAt: string;
}

/**
 * How a leased 10DLC number and the SMS registry line up.
 *
 * - `assigned` — leased, one X5 server sends from it.
 * - `shared` — leased, several servers under the *same* customer send from it.
 *   A normal multi-site setup, not a fault.
 * - `duplicate` — leased, two *different* customers claim it. Inbound replies
 *   cannot be attributed.
 * - `spare` — leased and paid for, nothing sends from it.
 * - `default` — the shared fallback every unregistered server sends from.
 * - `unleased` — a registry row points at a number AWS says we do not lease, so
 *   that customer's messages fall back to the shared default.
 */
export type SmsNumberStatus =
  | 'assigned'
  | 'shared'
  /** This number's server also holds another, so the gateway ignores both. */
  | 'conflicted'
  | 'duplicate'
  | 'spare'
  | 'default'
  | 'unleased';

export interface SmsNumberAssignment {
  serverIp: string;
  /** The free-text label on the registry row, as typed. */
  customer: string;
  /** Set only when the label matches an X5 organization name exactly. */
  organizationId: string | null;
  organizationName: string | null;
  /**
   * The EC2 instance answering on `serverIp`. Null when no instance in the
   * estate holds that address — a decommissioned server, or a stale row.
   */
  server: Ec2Server | null;
}

/** The EC2 instance running an X5 server, resolved from its stored address. */
export interface Ec2Server {
  instanceId: string;
  /** The `Name` tag, e.g. `x5-kymera-therapeutics`. */
  name: string | null;
  /** `running`, `stopped`, … — a stopped server cannot send anything. */
  state: string;
  /**
   * e.g. `User initiated (2025-02-26 16:29:27 GMT)` — the only place EC2 says
   * *when* an instance was stopped.
   */
  stateTransitionReason: string | null;
  instanceType: string | null;
  availabilityZone: string | null;
  privateIp: string | null;
  privateDnsName: string | null;
  launchTime: string | null;
  /**
   * Every non-`aws:` tag. This estate keeps its useful metadata here —
   * `TeleportHostname`, `DataExportName`, `Environment`, `validated`.
   */
  tags: { key: string; value: string }[];
}

export interface SmsLease {
  phoneNumber: string;
  phoneNumberId: string;
  status: string;
  numberType: string;
  capabilities: string[];
  isoCountryCode: string | null;
  monthlyPrice: number | null;
  twoWayEnabled: boolean;
  /** Null on a number that has never had two-way enabled. */
  twoWayChannelArn: string | null;
  optOutListName: string | null;
  poolId: string | null;
  registrationId: string | null;
  deletionProtection: boolean;
  createdAt: string | null;
}

export interface SmsNumber {
  phoneNumber: string;
  status: SmsNumberStatus;
  /** `duplicate` or `unleased` — the two states that need a human. */
  problem: boolean;
  assignments: SmsNumberAssignment[];
  /** Null exactly when `status` is `unleased`. */
  lease: SmsLease | null;
  /**
   * Other numbers on the same server. Non-empty means the gateway falls back to
   * the default for that server, so none of them is actually in use.
   */
  conflictsWith: string[];
  /** A leased, unassigned number a malformed row was probably meant to name. */
  suggestedNumber: string | null;
}

/** A leased number with nothing sending from it — offerable in the picker. */
export interface SmsCandidateNumber {
  phoneNumber: string;
  numberType: string;
}

/**
 * An X5 server that could take an assignment: tagged `Application=X5` +
 * `Environment=production` and not already holding a number. `label` is what
 * would be written to the registry row, computed server-side so what the picker
 * shows and what gets stored cannot diverge.
 */
export interface SmsCandidateServer {
  instanceId: string;
  name: string | null;
  label: string;
  serverIp: string;
  state: string;
  availabilityZone: string | null;
}

export interface SmsAssignmentSaved {
  assigned: true;
  phoneNumber: string;
  serverIp: string;
  customer: string;
  instanceId: string | null;
  replaced: { serverIp: string; phoneNumber: string } | null;
  changedBy: string;
  changedAt: string;
}

export interface SmsTwoWayUpdated {
  phoneNumber: string;
  twoWayEnabled: boolean;
  changedBy: string;
  changedAt: string;
}

export interface SmsAssignmentRemoved {
  removed: true;
  phoneNumber: string;
  serverIp: string;
  customer: string;
  /** What that server sends from now it has no row of its own. */
  fallsBackTo: string | null;
  changedBy: string;
  changedAt: string;
}

export interface SmsQuota {
  name: string;
  used: number;
  max: number;
}

export interface SmsCampaign {
  registrationId: string;
  registrationType: string;
  status: string;
  /** Leased numbers currently naming this campaign. */
  numbersHeld: number;
}

/**
 * The ceilings that decide whether another number can be leased. Null when the
 * limits could not be read — which is reported rather than assumed away, since
 * acquiring commits recurring spend.
 */
export interface SmsCapacity {
  phoneNumbers: SmsQuota | null;
  spendLimits: { name: string; enforced: number; max: number }[];
  /** COMPLETE campaign registrations only, emptiest first. */
  registrations: SmsCampaign[];
}

export interface SmsNumberAcquired {
  acquired: true;
  phoneNumber: string | null;
  phoneNumberId: string | null;
  registrationId: string;
  /** Always false: a new number arrives one-way. */
  twoWayEnabled: boolean;
  acquiredBy: string;
  acquiredAt: string;
}

export interface SmsNumbers {
  items: SmsNumber[];
  capacity: SmsCapacity | null;
  /** Only free, real options — never assembled from the rendered rows. */
  candidates: { numbers: SmsCandidateNumber[]; servers: SmsCandidateServer[] };
  summary: {
    leased: number;
    assigned: number;
    spare: number;
    problems: number;
    servers: number;
    customers: number;
    /** Assigned servers that are not running, so the number cannot send. */
    stoppedServers: number;
    /** Assigned servers whose IP no instance holds — the row is stale. */
    unresolvedServers: number;
    /** Servers holding more than one number; all are falling back today. */
    multiNumberServers: number;
    monthlyCost: number | null;
    spareMonthlyCost: number | null;
  };
  defaultOriginationNumber: string | null;
  registryTable: string;
  asOf: string;
  truncated: boolean;
  /** The X5 org scan capped, so some labels may show unlinked. */
  directoryTruncated: boolean;
}

export interface AppStreamUsageDay {
  date: string;
  sessions: number;
  users: number;
  seconds: number;
  /** Sessions that held an instance without anyone ever connecting. */
  abandoned: number;
}

export interface AppStreamUsageByUser {
  userId: string;
  organizationId: string | null;
  organizationName: string | null;
  internal: boolean;
  sessions: number;
  seconds: number;
  abandoned: number;
  lastSeen: string;
}

export interface AppStreamUsageByOrganization {
  organizationId: string | null;
  organizationName: string;
  internal: boolean;
  users: number;
  sessions: number;
  seconds: number;
  abandoned: number;
}

export interface AppStreamUsageByFleet {
  fleetName: string;
  stackName: string;
  sessions: number;
  users: number;
  seconds: number;
  abandoned: number;
}

/**
 * Read back out of the daily usage reports AppStream writes to S3. Each report
 * covers the sessions that *ended* that day, so a session spanning midnight
 * lands on its closing date.
 */
export interface AppStreamUsage {
  /** False when no usage-report subscription is configured. */
  enabled: boolean;
  days: number;
  daily: AppStreamUsageDay[];
  byUser: AppStreamUsageByUser[];
  byOrganization: AppStreamUsageByOrganization[];
  byFleet: AppStreamUsageByFleet[];
  totals?: {
    sessions: number;
    users: number;
    seconds: number;
    abandoned: number;
    longestSeconds: number;
  };
  topUsers?: number;
  /** Days with no report object at all — a gap, not a quiet day. */
  missingDays?: string[];
  directoryTruncated?: boolean;
}

/**
 * `on-cellular`: the router itself reports its WAN on mobile, recently.
 * `using-data`: the SIM moved more than keepalive traffic, whatever RMS says —
 * RMS goes weeks stale, Hologram's bytes do not.
 */
export type CellularActivity = 'on-cellular' | 'using-data' | 'standby' | 'inactive';

export interface CellularOrg {
  organizationId: string;
  organizationName: string;
}

export interface CellularRouter {
  id: number;
  name: string;
  model: string | null;
  serial: string | null;
  imei: string | null;
  wan: 'mobile' | 'wired' | 'unknown';
  fresh: boolean;
  connectionType: string | null;
  operator: string | null;
  signal: number | null;
  /** UTC, `YYYY-MM-DD HH:MM:SS`. */
  lastUpdateAt: string | null;
  serverIp: string | null;
  companyName: string | null;
  matchedBy: 'iccid' | 'imei';
  /** UTC `YYYY-MM-DD HH:MM:SS` the current 4G run began, from RMS history; only for routers on 4G now. */
  mobileSince: string | null;
  /** The run is older than RMS's history, so `mobileSince` is a lower bound. */
  mobileSinceBeyond: boolean;
}

export type CellularWanVerdict = 'disabled' | 'no-link' | 'no-address' | 'unreachable' | 'healthy' | 'unknown';

/** The fixed read-only WAN check run on a router through RMS. */
export interface CellularWanCheck {
  channel: string;
  routerId: number;
  /** `pending`: the router has not answered yet — poll with the channel. */
  state: 'pending' | 'done' | 'failed';
  output: string;
  check?: {
    disabled: boolean | null;
    device: string | null;
    link: boolean | null;
    up: boolean | null;
    ipv4: string | null;
    failover: string | null;
  };
  verdict?: CellularWanVerdict;
  summary?: string;
}

export interface CellularDay {
  date: string;
  bytes: number;
}

export interface CellularSim {
  deviceId: number;
  name: string;
  tag: string | null;
  iccid: string | null;
  linkId: number | null;
  state: string;
  plan: { id: number | null; name: string | null; perMb: number | null; monthly: number | null };
  activity: CellularActivity;
  activityReason: string;
  usage: {
    latestDay: number;
    last7d: number;
    baselineDaily: number;
    /** Bytes per day, aligned to `CellularSims.dates`. */
    daily: number[];
  };
  period: {
    bytes: number;
    cost: number | null;
    projectedCost: number | null;
    /** UTC, `YYYY-MM-DD HH:MM:SS`. */
    endsAt: string | null;
    previous: { index: number; bytes: number; cost: number | null }[];
  };
  router: CellularRouter | null;
  server: { ip: string; instanceId: string | null; name: string | null; state: string | null } | null;
  organization: CellularOrg | null;
  organizationCandidates: CellularOrg[];
  /** What the SIM rolls up under: its X5 org, else its Hologram tag. */
  customer: { key: string; label: string; fromTag: boolean };
  /** Dollars Hologram actually charged per day, aligned to `dates`; null until charges are loaded. */
  charged: number[] | null;
}

/** Actual spend from Hologram's statements and balance history. */
export interface CellularCharges {
  available: boolean;
  from: string | null;
  months: { month: string; charged: number; source: 'statement' | 'to-date' }[];
  monthToDate: number | null;
  /** Estimate: month-to-date plus the last 7 days' actual daily rate. */
  projectedMonth: number | null;
}

export interface CellularUnmatchedRouter {
  id: number;
  name: string;
  iccid: string | null;
  operator: string | null;
  wan: 'mobile' | 'wired' | 'unknown';
  fresh: boolean;
  lastUpdateAt: string | null;
}

export interface CellularSims {
  items: CellularSim[];
  unmatchedRouters: CellularUnmatchedRouter[];
  /** UTC days, oldest first. */
  dates: string[];
  latestDate: string | null;
  charges: CellularCharges;
  summary: {
    sims: number;
    retired: number;
    onCellular: number;
    usingData: number;
    standby: number;
    periodBytes: number;
    periodCost: number;
    projectedCost: number;
    previousPeriodCost: number;
    unpriced: number;
    routers: number;
    routersMatched: number;
    simsWithoutRouter: number;
    byPlan: {
      planId: number | null;
      planName: string | null;
      perMb: number | null;
      sims: number;
      periodCost: number | null;
    }[];
  };
  windowDays: number;
  activeDailyBytes: number;
  routersAvailable: boolean;
  /** When the cached Hologram + RMS snapshot was taken. */
  asOf: string;
  cacheTtlMinutes: number;
  /** A refresh was needed and failed; the data is the last good snapshot. */
  refreshFailed: boolean;
  staleParts: StalePart[];
  /** A background refresh is running; follow it with `getSnapshotRefresh`. */
  refreshRunId: string | null;
  /** Reading Hologram's charges failed on the last refresh; charges are older than `asOf`. */
  chargesStale: boolean;
  truncated: boolean;
  directoryPartial: boolean;
}

export type GatewayActionId =
  | 'status'
  | 'wireguard'
  | 'wan-check'
  | 'wan-show'
  | 'wan-disable'
  | 'wan-enable'
  | 'reboot';

export type GatewayPermission = 'gateway.query' | 'gateway.wan' | 'gateway.reboot';

export type GatewayCommandId =
  | 'rms.status'
  | 'rms.wan-check'
  | 'rms.wireguard'
  | 'rms.wan-config'
  | 'rms.wan-disable'
  | 'rms.wan-enable'
  | 'rms.reboot'
  | 'sms.status'
  | 'sms.wan-show'
  | 'sms.wan-disable'
  | 'sms.wan-enable'
  | 'sms.reboot';

/** `auto`: RMS first, then SMS if RMS cannot run it. */
export type GatewayRoute = 'auto' | 'rms' | 'sms';

/** One action on one channel. */
export interface GatewayCommandDef {
  id: GatewayCommandId;
  action: GatewayActionId;
  label: string;
  channel: 'rms' | 'sms';
  effect: 'read' | 'write';
  permission: GatewayPermission;
  needsPassword: boolean;
  /** `<serial>` / `<password>` are filled in by the server. */
  preview: string;
}

/** One entry in the server's fixed catalog, with whichever channels it exists on. */
export interface GatewayActionDef {
  id: GatewayActionId;
  label: string;
  description: string;
  effect: 'read' | 'write';
  permission: GatewayPermission;
  rms: GatewayCommandDef | null;
  sms: GatewayCommandDef | null;
}

export interface GatewaySendResult {
  /** One history row per channel tried, in order. */
  attempts: GatewayCommandRecord[];
  /** Why a channel was skipped or fell through. */
  notes: string[];
  /** `awaiting`: an SMS is out, or RMS has not answered yet — replies land in the history. */
  outcome: 'done' | 'awaiting' | 'failed';
  /** RMS did not deliver and SMS was not tried; offer it. */
  smsAvailable: boolean;
}

/**
 * `silent`: RMS has not heard from the gateway in two hours. Not "offline" —
 * it may be up with only its RMS check-in failing — so its last state is history.
 */
export type GatewayLink = 'mobile' | 'wired' | 'silent' | 'unknown';

export interface GatewayCheck {
  code: string;
  severity: 'problem' | 'note';
  message: string;
}

export interface GatewayRouter {
  id: number;
  name: string;
  model: string | null;
  serial: string | null;
  link: GatewayLink;
  /** UTC, `YYYY-MM-DD HH:MM:SS`. */
  lastUpdateAt: string | null;
  mobileSince: string | null;
  mobileSinceBeyond: boolean;
  signal: number | null;
  operator: string | null;
  connectionType: string | null;
  serverIp: string | null;
  server: { instanceId: string; name: string | null; state: string } | null;
  organization: CellularOrg | null;
  organizationCandidates: CellularOrg[];
  companyName: string | null;
  /** False for a gateway serving something other than X5 (AECOM) — no X5 server is expected. */
  x5: boolean;
  sim: { deviceId: number; name: string; state: string; iccid: string | null; matchedBy: 'iccid' | 'imei' } | null;
  smsReachable: boolean;
  /** How this gateway's SMS "status" rule authorizes: RUT955s by serial, others by the admin password. */
  smsStatusAuth: 'serial' | 'password';
  checks: GatewayCheck[];
}

export interface GatewayFleet {
  routers: GatewayRouter[];
  summary: { routers: number; onMobile: number; silent: number; misconfigured: number; smsReachable: number };
  actions: GatewayActionDef[];
  /** The fleet WireGuard dashboard in Grafana, when configured. */
  wireguardDashboardUrl: string | null;
  asOf: string;
  routersAvailable: boolean;
  refreshFailed: boolean;
  staleParts: StalePart[];
  /** A background refresh is running; follow it with `getSnapshotRefresh`. */
  refreshRunId: string | null;
  truncated: boolean;
  directoryPartial: boolean;
}

export type GatewayCommandStatus = 'sending' | 'pending' | 'done' | 'sent' | 'replied' | 'no-reply' | 'failed';

export interface GatewayCommandRecord {
  routerId: string;
  sentAt: string;
  timestamp: string;
  commandId: GatewayCommandId;
  action: GatewayActionId;
  label: string;
  channel: 'rms' | 'sms';
  effect: 'read' | 'write';
  actor: string;
  reason: string | null;
  /** What was sent, password replaced with `<password>`. */
  text: string;
  routerName: string;
  serial: string | null;
  deviceId: number | null;
  simName: string | null;
  customer: string | null;
  status: GatewayCommandStatus;
  reply?: string;
  replyAt?: string;
  replyCount?: number;
  /** The network confirmed the SMS reached the SIM — not an answer from the gateway. */
  deliveredAt?: string;
  verdict?: CellularWanVerdict | WireguardVerdict;
  summary?: string;
  /** Parsed tunnels, for a WireGuard check. */
  wireguard?: WireguardPeer[];
  error?: string;
  settledAt?: string;
}

export type WireguardVerdict = 'up' | 'down' | 'never' | 'none' | 'unknown';

export interface WireguardPeer {
  iface: string;
  peer: string;
  handshakeAt: number | null;
  /** By the gateway's own clock. */
  handshakeAgeS: number | null;
  endpoint: string | null;
  rxBytes: number | null;
  txBytes: number | null;
  /** The interface the gateway sends the tunnel over — which link it is really on. */
  routeDev: string | null;
  link: 'mobile' | 'wired' | null;
  state: 'up' | 'down' | 'never';
}

export interface GatewayCommandLog {
  items: GatewayCommandRecord[];
  /** More commands exist than this answer holds — the row cap was hit before the range ran out. */
  truncated: boolean;
  scannedThrough: string;
  days: number;
}

/** One Hologram read that failed on the last refresh; `since` is how old the copy kept is. */
export interface StalePart {
  part: 'sims' | 'billing' | 'daily';
  since: string;
}

const STALE_PART_LABEL: Record<StalePart['part'], string> = {
  sims: 'SIM list',
  billing: 'billing-period usage',
  daily: 'daily usage',
};

export function staleNotice(parts: StalePart[], formatAge: (iso: string) => string): string | null {
  if (parts.length === 0) return null;
  const list = parts.map((p) => `${STALE_PART_LABEL[p.part]} (from ${formatAge(p.since)})`).join(', ');
  return `Hologram did not answer for its ${list}, so that part is the previous copy; everything else is fresh. It is retried automatically within 5 minutes.`;
}

export type RefreshStep =
  | 'snapshot'
  | 'rms'
  | 'sims'
  | 'billing'
  | 'daily'
  | 'rates'
  | 'ledger'
  | 'mobileSince'
  | 'store';

/** `stale`: that read failed and the previous copy was kept. */
export type RefreshStepState = 'pending' | 'running' | 'done' | 'stale' | 'failed';

/** Live progress of one background Hologram + RMS refresh. */
export interface RefreshProgress {
  runId: string;
  startedAt: number;
  finishedAt?: number;
  state: 'running' | 'done' | 'failed';
  steps: Record<RefreshStep, { state: RefreshStepState; startedAt?: number; endedAt?: number; note?: string }>;
  error?: string;
}

/** `undecided` until someone chooses; `declined` is "don't notify". */
export type NoticeState = 'undecided' | 'notify' | 'declined' | 'resolved';

export interface NoticeRecipient {
  name: string;
  email: string;
}

export interface NoticeEvent {
  at: string;
  type: 'opened' | 'decision' | 'email' | 'email-failed' | 'test' | 'resolved';
  /** Absent for the nightly run. */
  actor?: string;
  detail?: string;
  messageId?: string;
}

export interface NoticeCase {
  customerKey: string;
  openedAt: string;
  label: string;
  organizationId: string | null;
  state: NoticeState;
  recipients: NoticeRecipient[];
  decidedBy?: string;
  decidedAt?: string;
  reason?: string;
  lastEmailAt?: string;
  emailCount: number;
  resolvedAt?: string;
  events: NoticeEvent[];
  rev: number;
}

export interface NoticeSiteUsage {
  deviceId: number;
  name: string;
  onFourG: boolean;
  daysOnFourG: number;
  last30Bytes: number;
}

export interface NoticeUsage {
  key: string;
  label: string;
  organizationId: string | null;
  month: string;
  monthBytes: number;
  /** Actual charges this month; null when charges are not loaded. */
  monthCharged: number | null;
  /** Estimate. */
  projectedCharged: number | null;
  rankByData: number | null;
  rankByCharged: number | null;
  onFourG: boolean;
  daysOnFourG: number;
  last30Bytes: number;
  last30Charged: number | null;
  excessive: 'over' | 'projected' | null;
  flagged: boolean;
  sites: NoticeSiteUsage[];
}

export type NoticeNextStep =
  | { kind: 'decide' }
  | { kind: 'not-notifying' }
  | { kind: 'add-recipients' }
  | { kind: 'waiting-days'; daysLeft: number }
  | { kind: 'due'; email: 'initial' | 'reminder' }
  | { kind: 'reminder-on'; at: string };

export interface NoticeSettings {
  topN: number;
  excessiveUsd: number;
  minDaysOnFourG: number;
  reminderDays: number;
  timeZone: string;
  sendTime: string;
  from: string;
  cc: string[];
  safetyNetEmail: string;
  billingEmail: string;
}

export interface CellularNoticeList {
  rows: { key: string; usage: NoticeUsage | null; notice: NoticeCase | null; next: NoticeNextStep }[];
  customers: { key: string; label: string }[];
  month: string;
  asOf: string;
  settings: NoticeSettings;
}

export interface CellularNoticeDetail {
  key: string;
  usage: NoticeUsage | null;
  /** Every case for this customer, newest first. */
  history: NoticeCase[];
  next: NoticeNextStep | null;
  preview: { kind: 'initial' | 'reminder'; subject: string; html: string; text: string } | null;
  settings: NoticeSettings;
}
